import { use, useEffect, useMemo } from 'react';
import { ListContextProvider, useAuthenticated, useGetList, useGetOne, useListController, useStore } from 'react-admin';
import { ZipDataMap } from './components/zip-data-map/zip-data-map';
import { ZipDataDto, ZipDataDistribution } from '../../models/zip-data';
import { DataStats } from "../../models/data-stats";
import { GlobalMapFilter } from '../../components/global-map-filter/global-map-filter';
import { useDateRange } from '../../hooks/use-date-range';
import { useMapAttributeType } from '../../hooks/use-map-attribute';
import { Box } from '@mui/material';
import { RegionInfoPanel } from '../../components/region-info-panel/region-info-panel';
import { RegionInfo } from '../../models/region';


const styles = {
  flex: { display: 'flex' },
  flexColumn: { display: 'flex', flexDirection: 'column' },
  leftCol: { flex: 1, marginRight: '0.5em', display: 'flex', flexDirection: 'column' },
  rightCol: { flex: 5, marginLeft: '0.5em' },
};

export type DataFilter = {
  type: 'sale' | 'rent';
  from: string,
  to: string;
}

export const ZipMapPage = () => {
  const [fromDate, toDate] = useDateRange();
  const [mapAttributeType] = useMapAttributeType();
  const { isPending: isPendingUser } = useAuthenticated();
  
  // Reset region info when loading new page
  const [region, setRegion] = useStore<RegionInfo | undefined>('region.active', undefined);
  useEffect(() => {
    if (region) setRegion(undefined);
  }, []);

  const dataFilter = useMemo<DataFilter>(() => {
    return {
      type: mapAttributeType.toString(),
      from: fromDate,
      to: toDate
    } as DataFilter;
  }, [fromDate, toDate, mapAttributeType]);

  const listContext = useListController<ZipDataDto>({
    perPage: 9999,
    filter: {
      type: dataFilter.type,
      from: dataFilter.from,
      to: dataFilter.to
    }
  });
  const { data: zipData, isFetching, isLoading } = listContext;

  const zipDataDistribution = useMemo<ZipDataDistribution>(() => {
    if (!zipData) return {};
    const aggregations = zipData
      .reduce(
        (stats: DataStats, data) => {
          if (stats.dataMin > data.dataMean)
            stats.dataMin = data.dataMean;
          if (stats.dataMax < data.dataMean)
            stats.dataMax = data.dataMean;
          if (data.dataCount > stats.dataCountMax)
            stats.dataCountMax = data.dataCount;
          stats.dataMean += data.dataMean;
          return stats;
        },
        {
          dataMin: zipData[0]?.dataMean ?? 0,
          dataMax: 0,
          dataMean: 0,
          dataCountMax: 0,
        }
      );
    aggregations.dataMean /= zipData.length;
    return {
      zipData: zipData,
      stats: aggregations
    };
  }, [zipData]);

  //if (isLoading || isPendingUser) {
  if (isPendingUser) {
    return <><p>Loading...</p></>;
  }

  return (
    <>
      {/* <Welcome /> */}
      <GlobalMapFilter dataStats={zipDataDistribution.stats} mapDataType='zip' />
      <ListContextProvider value={listContext}>
        <Box style={styles.flex}>
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: '1' }}>
            <Box sx={{ borderTop: '1px solid white', padding: '1em', marginTop: '1em' }}>
              <RegionInfoPanel />
            </Box>
          </Box>
          <Box style={styles.rightCol}>
            <ZipDataMap data={zipDataDistribution} isLoading={isLoading || isFetching} />
          </Box>
        </Box>
      </ListContextProvider>
    </>
  );
};
